import React from 'react';
import { Button } from '@heroui/react';
import { TriangleAlert } from 'lucide-react';

/**
 * BootstrapError — friendly error screen shown when `fetchBootstrap` rejects.
 * Mirrors the Pencil "Error State" frame (`month-view.pen` node `Z1wzj`):
 * 320×320 card, 48×48 danger-tinted circle icon, title in danger, centered
 * secondary subtitle (max 240px), primary Retry button.
 *
 * Shared by Month, Week, Day and the Finder — `viewName` names the actual
 * view in the title (defaults to `'Month View'` for backward-compat with
 * call sites that don't pass it yet).
 *
 * The raw error object is logged by the thunk so on-call / Sentry retain
 * full technical detail — this component shows only a user-facing string.
 */
function BootstrapError({ error, onRetry, viewName = 'Month View' }) {
	return (
		<div
			className="flex flex-col items-center justify-center gap-3 mx-auto p-5"
			style={{ maxWidth: 320, minHeight: 240 }}
			data-testid="month-view-bootstrap-error"
			role="alert"
			aria-live="assertive"
		>
			<div
				className="flex items-center justify-center rounded-full bg-danger-50"
				style={{ width: 48, height: 48 }}
			>
				<TriangleAlert size={24} className="text-danger" aria-hidden="true" />
			</div>

			<p
				className="font-semibold text-danger text-center"
				style={{ fontSize: 15, lineHeight: 1.25 }}
			>
				Failed to load {viewName}
			</p>

			<p
				className="text-xs text-default-500 text-center"
				style={{ maxWidth: 240, lineHeight: 1.4 }}
			>
				{error || 'Network request failed. Check your connection and try again.'}
			</p>

			{typeof onRetry === 'function' && (
				<Button color="primary" size="sm" className="rounded-[8px] font-medium" onPress={onRetry}>
					Retry
				</Button>
			)}
		</div>
	);
}

export default BootstrapError;
